import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, VStack, Divider } from "@chakra-ui/react";

import { Form, Input } from "components";
import { User, UserRole } from "types/user";
import { useUser } from "context/user";
import { fetchAdminUsers } from "api/webservice/users";
import { Organization } from "types/organization";
import { fetchOrganizations } from "api/webservice/organizations";

interface Props {
  user?: User;
  saveUser: any;
  deleteUser: any;
}

type FormData = {
  organization_id: string;
  username: string;
  email: string;
  privs: number;
  first_name: string;
  last_name: string;
  copyFrom: string;
};

const ProfileForm: React.FC<Props> = ({
  user,
  saveUser,
  deleteUser
}: Props) => {
  const { user: actor } = useUser()!;
  const {
    handleSubmit,
    errors,
    register,
    formState,
    setValue
  } = useForm<FormData>({
    defaultValues: user
  });
  const [coaches, setCoaches] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  useEffect(() => {
    fetchAdminUsers().then(response => {
      setCoaches(response);
    });
  }, [setCoaches]);

  useEffect(() => {
    fetchOrganizations().then(response => {
      setOrganizations(response);
      if (user) setValue("organization_id", user?.organization_id);
    });
  }, [setOrganizations]);

  const validateOrg = (value: string) => {
    if (!value) {
      return "Organization is required";
    } else return true;
  };

  const validateUsername = (value: string) => {
    if (!value) {
      return "Username is required";
    } else return true;
  };

  const validateFirst_name = (value: string) => {
    if (!value) {
      return "First name is required";
    } else return true;
  };

  const validateLast_name = (value: string) => {
    if (!value) {
      return "Last name is required";
    } else return true;
  };

  const validateEmail = (value: string) => {
    if (!value) {
      return "Email is required";
    } else return true;
  };

  const validatePrivs = (value: number) => {
    if (!value) {
      return "Priv level is required";
    } else if (user && user.privs >= actor.privs) {
      return "Cannot modify equal or higher priveleged user";
    } else if (value > actor.privs) {
      return "Cannot promote user above yourself";
    } else return true;
  };

  const submitForm = async ({
    copyFrom,
    ...rest
  }: FormData): Promise<any | null> => {
    saveUser(
      {
        id: user?.id,
        ...rest
      },
      copyFrom
    );
  };

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <VStack spacing={4}>
        <Input
          size="md"
          type="select"
          name="organization_id"
          label="Organization"
          register={register({ validate: validateOrg })}
          required
          errorMessage={errors.organization_id?.message}
        >
          {organizations.map(organization => (
            <option value={organization.id} key={organization.id}>
              {organization.displayName || organization.name}
            </option>
          ))}
        </Input>

        <Input
          size="md"
          name="first_name"
          label="First Name"
          register={register({ validate: validateFirst_name })}
          required
          errorMessage={errors.first_name?.message}
        />

        <Input
          size="md"
          name="last_name"
          label="Last Name"
          register={register({ validate: validateLast_name })}
          required
          errorMessage={errors.last_name?.message}
        />

        <Input
          size="md"
          name="username"
          label="Username"
          register={register({ validate: validateUsername })}
          required
          errorMessage={errors.username?.message}
        />

        <Input
          size="md"
          type="email"
          name="email"
          label="Email"
          register={register({ validate: validateEmail })}
          required
          errorMessage={errors.email?.message}
        />

        <Input
          size="md"
          name="privs"
          type="select"
          label="Priv Level"
          register={register({ validate: validatePrivs })}
          required
          errorMessage={errors.privs?.message}
        >
          {Object.entries(UserRole)
            .filter(element => isNaN(Number(element[0])))
            .filter(element => element[1] > UserRole.Client)
            .filter(element => actor && element[1] <= actor?.privs)
            .map(role => (
              <option value={role[1]} key={role[0]}>
                {role[0]}
              </option>
            ))}
        </Input>

        {!user && (
          <Input
            size="md"
            placeholder=" "
            name="copyFrom"
            type="select"
            label="Copy Avatars From"
            register={register()}
            errorMessage={errors.copyFrom?.message}
          >
            {coaches.map(coach => (
              <option
                value={coach.id}
                key={coach.id}
              >{`${coach.first_name} ${coach.last_name}`}</option>
            ))}
          </Input>
        )}

        <Divider maxW="150px" borderWidth="1px" rounded="full" />
        <Button
          disabled={formState.isSubmitting}
          type="submit"
          width="full"
          size="md"
        >
          Save Profile
        </Button>
        {actor?.privs >= UserRole.Admin && (
          <Button
            variant="link"
            size="sm"
            color="red.500"
            onClick={() => deleteUser(user)}
          >
            Delete User
          </Button>
        )}
      </VStack>
    </Form>
  );
};

export default ProfileForm;
